import { SlidersHorizontal } from "@/components/ui/icons";
import { Pressable, Text } from "react-native";

import { cn } from "@/lib/ui/cn";
import { slopToFloor } from "@/lib/ui/touchTarget";
import { useThemeColors } from "@/theme/theme";

/** The painted height, matching `FilterPill` and `SortPill`. */
const TRIGGER_HEIGHT = 36;

/**
 * The "Filters" trigger every filtered list shares (inventory, labels, the storefronts): sliders,
 * the word, and the applied count, tinted while anything is on.
 *
 * Sized like `FilterPill`, which has the same shape: `min-h-9` sets a 36pt paint and `slopToFloor`
 * extends the touch target to the platform floor. Padding alone paints about 26pt, and a flat slop
 * large enough to compensate would reach into the row below, where RN hit-tests whichever view is on
 * top.
 *
 * `compact` drops the word for a crowded row (inventory's selection mode, or a phone too narrow for
 * the tool row), keeping the count.
 */
export function FiltersTrigger(
  { count, onPress, testID, expanded, compact = false, className }: {
    count: number;
    onPress: () => void;
    testID?: string;
    /** For a trigger that toggles an anchored popover rather than opening a modal sheet. */
    expanded?: boolean;
    compact?: boolean;
    className?: string;
  },
) {
  const c = useThemeColors();
  const on = count > 0;
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      hitSlop={slopToFloor(TRIGGER_HEIGHT)}
      accessibilityRole="button"
      accessibilityLabel={on ? `Filters, ${count} active` : "Filters"}
      accessibilityState={expanded === undefined ? undefined : { expanded }}
      className={cn(
        "min-h-9 flex-row items-center justify-center gap-1 rounded-full border py-1.5",
        compact ? "min-w-9 px-2" : "px-3",
        on ? "border-primary bg-accent-soft" : "border-border bg-surface",
        className,
      )}
    >
      <SlidersHorizontal size={14} color={on ? c.primary : c.mutedForeground} aria-hidden />
      {!compact || on
        ? (
          <Text className={cn("font-label text-xs", on ? "text-primary" : "text-muted-foreground")}>
            {compact ? count : on ? `Filters · ${count}` : "Filters"}
          </Text>
        )
        : null}
    </Pressable>
  );
}
